import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Users, TrendingUp, TrendingDown, DollarSign, ShoppingCart,
  ArrowUpRight, ArrowDownRight, Trash2, Plus, X, ChevronLeft, ChevronRight,
  Mail, RefreshCw, AlertTriangle, Settings, Edit2, PlusCircle,
} from 'lucide-react';
import './RyanAdminSales.css';
import RyanMonthlyLineChart from '../../../components/RyanMonthlyLineChart';

// AccessTab, OverviewTab e ProfitabilityTab são exportadas nomeadas e
// consumidas pelo RyanAdminHub (casca de navegação unificada — ver
// admin/components/tabs/RyanAdminHub.tsx). Este arquivo não tem mais um
// componente-página próprio com sua própria barra de abas: RyanAccessRequests
// (agora vive dentro de RyanAdminSettings, como "Aprovações manuais (legado)")
// e RyanRepresentatives (importada direto pelo Hub) saíram daqui porque só
// eram usadas pela casca antiga.
const tok = () => localStorage.getItem('accessToken') || '';
const api = (path: string, opts?: RequestInit) =>
  fetch(`/api/ryan${path}`, { ...opts, headers: { Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json', ...(opts?.headers || {}) } });
const cents = (c: number) => `R$ ${(c / 100).toFixed(2).replace('.', ',')}`;
const fmtDate = (d: string | Date | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR');
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface Product { _id: string; title: string; periodo: string | null; priceCents: number; grantsInstitutionTags?: string[]; }
interface SpaceGroupRef { _id: string; name: string; requiredInstitutionTag?: string | null; }
interface SaleOverview {
  totalRevenueCents: number; totalSales: number; salesToday: number;
  revenueTodayCents: number; averageTicketCents: number;
}
interface ChartDay { date: string; salesCount: number; revenueCents: number; }
interface YearMonth { month: string; salesCount: number; revenueCents: number; }
interface Trending {
  rising:  { productId: string; title: string; currentSales: number; previousSales: number; growthPercent: number }[];
  falling: { productId: string; title: string; currentSales: number; previousSales: number; growthPercent: number }[];
}
interface UserAccess {
  _id: string; email: string;
  userId?: { _id: string; username: string; email: string } | null;
  grants: { type: 'space' | 'tab' | 'subdeck'; refId: any; expiresAt?: string | null; productId?: string | null }[];
  products: { _id: string; title: string; periodo: string | null; expiresAt: string | null }[];
  isVip: boolean; isEditor: boolean;
  institutionTags?: string[];
}
interface PendingInvite {
  _id: string; email: string;
  productId: { _id: string; title: string } | null;
  status: string; createdAt: string; expiresAt: string;
}
interface Profitability {
  totalRevenueCents: number; totalExpensesCents: number; netProfitCents: number;
  margin: number; totalSales: number;
  avgRevenuePerSale: number; avgExpensePerSale: number; avgProfitPerSale: number;
}
interface Expense {
  _id: string; type: string; description: string; amountCents: number;
  recurrence: string; date: string; notes: string;
}
interface ProfitChart { period: string; revenue: number; expenses: number; profit: number; }
interface SaleEntry {
  _id: string; email: string;
  userId?: { _id: string; username: string; email: string } | null;
  productId?: { _id: string; title: string; periodo: string } | null;
  priceCents: number; countAsRevenue: boolean; status: string;
  grantedBy?: { _id: string; username: string } | null;
  grantedAt: string; notes: string;
  durationDays?: number | null;
  expiresAt?: string | null;
}

// ─── Duration options ─────────────────────────────────────────────────────────

const DURATION_OPTIONS = [
  { label: '1 semana', days: 7 },
  { label: '15 dias',  days: 15 },
  { label: '1 mês',   days: 30 },
  { label: '45 dias',  days: 45 },
  { label: '2 meses',  days: 60 },
  { label: '3 meses',  days: 90 },
  { label: '4 meses',  days: 120 },
];

type ExpiryVariant = 'normal' | 'warning' | 'expired' | 'no-expiry';

function getExpiryInfo(expiresAt: string | null | undefined): { text: string; variant: ExpiryVariant; warn: boolean } {
  if (!expiresAt) return { text: 'Sem expiração', variant: 'no-expiry', warn: false };
  const daysLeft = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (daysLeft <= 0)  return { text: 'Expirado',          variant: 'expired', warn: true };
  if (daysLeft <= 7)  return { text: `${daysLeft}d`,      variant: 'warning', warn: true };
  return { text: `${daysLeft}d restantes`, variant: 'normal', warn: false };
}

// ─── Aba Acesso ───────────────────────────────────────────────────────────────

export const AccessTab: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [spaceGroups, setSpaceGroups] = useState<SpaceGroupRef[]>([]);
  const [users, setUsers]       = useState<UserAccess[]>([]);
  const [invites, setInvites]   = useState<PendingInvite[]>([]);
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  // 24 fecha linhas cheias no grid auto-fill (2, 3, 4, 6 ou 8 colunas) —
  // 21 deixava 3 buracos na última linha com 4 colunas
  const PAGE_SIZE = 24;

  // Manage access modal
  const [managingUser, setManagingUser] = useState<UserAccess | null>(null);
  const [mgProductId, setMgProductId]   = useState('');
  const [mgDuration, setMgDuration]     = useState(30);
  const [mgCountRev, setMgCountRev]     = useState(false);
  const [mgAdding, setMgAdding]         = useState(false);
  const [mgRevokeConfirm, setMgRevokeConfirm] = useState(false);

  // União de tags já em uso no sistema (produtos + grupos) — mesma derivação
  // de knownInstitutionTags em RyanAdminProducts.tsx, mas recalculada aqui
  // porque são componentes/telas diferentes.
  const knownInstitutionTags = useMemo(() => {
    const tags = new Set<string>();
    products.forEach(p => (p.grantsInstitutionTags || []).forEach(t => t && tags.add(t)));
    spaceGroups.forEach(g => { if (g.requiredInstitutionTag) tags.add(g.requiredInstitutionTag); });
    return [...tags].sort();
  }, [products, spaceGroups]);

  // User grid filters (client-side)
  const [accessFilter, setAccessFilter] = useState<'all' | 'with' | 'without' | 'vip' | 'editor'>('all');
  const [productFilter, setProductFilter] = useState('');

  const loadUsers = useCallback(async (p = page, q = search) => {
    setLoading(true);
    const r = await api(`/admin/sales/users-access?page=${p}&limit=${PAGE_SIZE}&search=${encodeURIComponent(q)}`);
    if (r.ok) { const d = await r.json(); setUsers(d.items || []); setTotal(d.total || 0); }
    setLoading(false);
  }, [page, search]);

  const loadInvites = useCallback(async () => {
    const r = await api('/admin/sales/invites');
    if (r.ok) { const d = await r.json(); setInvites(d.invites || []); }
  }, []);

  useEffect(() => {
    api('/admin/products').then(r => r.ok ? r.json() : { products: [] }).then(d => setProducts(d.products || d || []));
    api('/admin/space-groups').then(r => r.ok ? r.json() : []).then(d => setSpaceGroups(Array.isArray(d) ? d : []));
  }, []);

  useEffect(() => { loadUsers(page, search); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); loadUsers(1, search); }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadInvites(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync managingUser when users list refreshes
  useEffect(() => {
    if (managingUser) {
      const updated = users.find(u => u._id === managingUser._id);
      if (updated) setManagingUser(updated);
    }
  }, [users]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelInvite = async (id: string) => {
    await api(`/admin/sales/invites/${id}`, { method: 'DELETE' });
    loadInvites();
  };

  const handleProductAction = async (
    accessId: string, pid: string, action: 'add' | 'remove',
    opts?: { durationDays?: number; countAsRevenue?: boolean }
  ) => {
    await api('/admin/sales/users-access/' + accessId + '/product', {
      method: 'PATCH',
      body: JSON.stringify({ productId: pid, action, countAsRevenue: opts?.countAsRevenue ?? false, durationDays: opts?.durationDays }),
    });
    loadUsers(page, search);
  };

  const handleToggleVip = async (accessId: string, val: boolean) => {
    await api(`/admin/accesses/${accessId}/vip`, { method: 'PATCH', body: JSON.stringify({ isVip: val }) });
    setManagingUser(u => u ? { ...u, isVip: val } : u);
    loadUsers(page, search);
  };

  const handleToggleEditor = async (accessId: string, val: boolean) => {
    await api(`/admin/accesses/${accessId}/editor`, { method: 'PATCH', body: JSON.stringify({ isEditor: val }) });
    setManagingUser(u => u ? { ...u, isEditor: val } : u);
    loadUsers(page, search);
  };

  // Substituição do array inteiro (não união) — igual VIP/Editor são
  // substituição de boolean, esta é edição administrativa direta, nunca
  // passa por applyProductGrants/grants (ver PATCH .../institution-tags).
  const toggleInstitutionTag = async (accessId: string, tag: string, currentTags: string[]) => {
    const next = currentTags.includes(tag) ? currentTags.filter(t => t !== tag) : [...currentTags, tag];
    await api(`/admin/accesses/${accessId}/institution-tags`, {
      method: 'PATCH', body: JSON.stringify({ institutionTags: next }),
    });
    setManagingUser(u => u ? { ...u, institutionTags: next } : u);
    loadUsers(page, search);
  };

  const handleRevokeAll = async (accessId: string) => {
    await api(`/admin/sales/users-access/${accessId}/products`, { method: 'DELETE' });
    setMgRevokeConfirm(false);
    loadUsers(page, search);
  };

  const openManage = (u: UserAccess) => {
    setManagingUser(u);
    setMgProductId(''); setMgDuration(30); setMgCountRev(false);
    setMgRevokeConfirm(false); setMgAdding(false);
  };

  const closeManage = () => { setManagingUser(null); setMgRevokeConfirm(false); };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const missingProducts = managingUser
    ? products.filter(p => !managingUser.products.find(up => up._id.toString() === p._id))
    : [];

  const filteredUsers = users.filter(u => {
    const hasAccess = u.products.length > 0 || u.isVip || u.isEditor;
    if (accessFilter === 'with' && !hasAccess) return false;
    if (accessFilter === 'without' && hasAccess) return false;
    if (accessFilter === 'vip' && !u.isVip) return false;
    if (accessFilter === 'editor' && !u.isEditor) return false;
    if (productFilter && !u.products.some(p => p._id === productFilter)) return false;
    return true;
  });

  return (
    <div className="ras-pane">
      {/* Pending invites */}
      {invites.length > 0 && (
        <div className="ras-section">
          <div className="ras-section-title">Convites pendentes ({invites.length})</div>
          <div className="ras-invite-list">
            {invites.map(inv => (
              <div key={inv._id} className="ras-invite-row">
                <Mail size={14} className="ras-invite-icon" />
                <span className="ras-invite-email">{inv.email}</span>
                <span className="ras-invite-product">{inv.productId?.title || '—'}</span>
                <span className="ras-invite-exp">expira {fmtDate(inv.expiresAt)}</span>
                <button className="ras-invite-cancel" onClick={() => cancelInvite(inv._id)} title="Cancelar">
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User list */}
      <div className="ras-user-section">
        <div className="ras-section-header" style={{ padding: '0 8px 4px' }}>
          <div className="ras-section-title">Usuários com acesso</div>
          <div className="ras-search-row">
            <input className="ras-input" placeholder="Buscar por email ou nome…" value={search} onChange={e => setSearch(e.target.value)} />
            <span className="ras-total-label">{total} usuário{total !== 1 ? 's' : ''}</span>
            <button className="ras-icon-btn" onClick={() => loadUsers(page, search)}><RefreshCw size={15} /></button>
          </div>
        </div>
        <div className="ras-access-filters" style={{ padding: '0 8px' }}>
          <div className="ras-filter-group">
            {(['all', 'with', 'without', 'vip', 'editor'] as const).map(f => (
              <button key={f}
                className={`ras-filter-chip${accessFilter === f ? ' active' : ''}`}
                onClick={() => setAccessFilter(f)}>
                {{ all: 'Todos', with: 'Com produto', without: 'Sem produto', vip: 'VIP', editor: 'Editor' }[f]}
              </button>
            ))}
          </div>
          <select className="ras-input ras-input--sm" value={productFilter} onChange={e => setProductFilter(e.target.value)}
            style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }}>
            <option value="">Todos os produtos</option>
            {products.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
          </select>
        </div>
        {loading ? <div className="ras-spinner-wrap"><div className="ras-spinner" /></div> : (
          <div className="ras-user-grid">
            {filteredUsers.length === 0 && <div className="ras-empty ras-empty--grid">Nenhum usuário encontrado.</div>}
            {filteredUsers.map(u => {
              const name = u.userId?.username || u.email;
              return (
                <div key={u._id} className="ras-user-card">
                  <div className="ras-user-row">
                    <div className="ras-user-avatar">{name[0].toUpperCase()}</div>
                    <div className="ras-user-info">
                      <span className="ras-user-name">{name}</span>
                      {u.userId?.username && <span className="ras-user-email">{u.email}</span>}
                    </div>
                    <button className="ras-manage-btn" onClick={() => openManage(u)} title="Gerenciar">
                      <Settings size={12} /> <span className="ras-manage-btn-label">Gerenciar</span>
                    </button>
                  </div>
                  <div className="ras-user-badges-row">
                    {u.isVip    && <span className="ras-badge ras-badge--vip">VIP</span>}
                    {u.isEditor && <span className="ras-badge ras-badge--editor">Editor</span>}
                    {u.products.map(p => {
                      const exp = getExpiryInfo(p.expiresAt);
                      return (
                        <span key={p._id} className={`ras-product-chip ras-product-chip--${exp.variant}`}>
                          {p.title}
                          {exp.warn && <AlertTriangle size={10} />}
                          <span className="ras-chip-sep">·</span>
                          {exp.text}
                        </span>
                      );
                    })}
                    {u.products.length === 0 && !u.isVip && !u.isEditor && (
                      <span className="ras-badge ras-badge--none">Sem produto</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {totalPages > 1 && (
          <div className="ras-pagination" style={{ padding: '0 8px' }}>
            <button className="ras-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={16} /></button>
            <span className="ras-page-info">{page} / {totalPages}</span>
            <button className="ras-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={16} /></button>
          </div>
        )}
      </div>

      {/* Management modal */}
      {managingUser && ReactDOM.createPortal(
        <div className="ras-modal-overlay" onClick={closeManage}>
          <div className="ras-modal" onClick={e => e.stopPropagation()}>
            <div className="ras-modal-header">
              <div>
                <div className="ras-modal-title">{managingUser.userId?.username || managingUser.email}</div>
                {managingUser.userId?.username && <div className="ras-modal-subtitle">{managingUser.email}</div>}
              </div>
              <button className="ras-modal-close" onClick={closeManage}><X size={18} /></button>
            </div>

            <div className="ras-modal-body">
              {/* Section A: Active products */}
              <div className="ras-modal-section">
                <div className="ras-modal-section-label">Produtos ativos</div>
                {managingUser.products.length === 0
                  ? <div className="ras-modal-empty">Nenhum produto ativo.</div>
                  : managingUser.products.map(p => {
                    const exp = getExpiryInfo(p.expiresAt);
                    return (
                      <div key={p._id} className="ras-modal-product-row">
                        <div className="ras-modal-product-meta">
                          <span className="ras-modal-product-name">{p.title}</span>
                          <span className={`ras-product-chip ras-product-chip--${exp.variant}`}>
                            {exp.warn && <AlertTriangle size={9} />}
                            {exp.text}
                          </span>
                        </div>
                        <div className="ras-expiry-bar">
                          <div className={`ras-expiry-fill ras-expiry-fill--${exp.variant}`} />
                        </div>
                        <button className="ras-remove-btn"
                          onClick={() => handleProductAction(managingUser._id, p._id.toString(), 'remove')}>
                          <X size={11} /> Remover
                        </button>
                      </div>
                    );
                  })}
              </div>

              {/* Section B: Add product */}
              <div className="ras-modal-section">
                <div className="ras-modal-section-label">Adicionar produto</div>
                {missingProducts.length === 0
                  ? <div className="ras-modal-empty">Todos os produtos já estão ativos.</div>
                  : (
                    <div className="ras-modal-add-form">
                      <select className="ras-input" value={mgProductId} onChange={e => setMgProductId(e.target.value)}>
                        <option value="">Selecionar produto…</option>
                        {missingProducts.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
                      </select>
                      <div className="ras-duration-section">
                        <span className="ras-duration-label">Duração</span>
                        <div className="ras-duration-chips">
                          {DURATION_OPTIONS.map(opt => (
                            <button key={opt.days} type="button"
                              className={`ras-duration-chip${mgDuration === opt.days ? ' active' : ''}`}
                              onClick={() => setMgDuration(opt.days)}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="ras-form-row">
                        <label className="ras-toggle-label">
                          <input type="checkbox" checked={mgCountRev} onChange={e => setMgCountRev(e.target.checked)} />
                          Contar como receita
                        </label>
                        <button className="ras-grant-btn" disabled={!mgProductId || mgAdding}
                          onClick={async () => {
                            if (!mgProductId) return;
                            setMgAdding(true);
                            await handleProductAction(managingUser._id, mgProductId, 'add', { durationDays: mgDuration, countAsRevenue: mgCountRev });
                            setMgProductId(''); setMgAdding(false);
                          }}>
                          {mgAdding ? 'Adicionando…' : <><Plus size={13} /> Adicionar</>}
                        </button>
                      </div>
                    </div>
                  )}
              </div>

              {/* Section C: Permissions */}
              <div className="ras-modal-section">
                <div className="ras-modal-section-label">Permissões</div>
                <div className="ras-modal-perm-row">
                  <div className="ras-modal-perm-info">
                    <span className="ras-modal-perm-name">VIP</span>
                    <span className="ras-modal-perm-desc">Acesso a todos os campos e recursos</span>
                  </div>
                  <button type="button" className={`ras-sw${managingUser.isVip ? ' on' : ''}`}
                    onClick={() => handleToggleVip(managingUser._id, !managingUser.isVip)}>
                    <span className="ras-sw-knob" />
                  </button>
                </div>
                <div className="ras-modal-perm-row">
                  <div className="ras-modal-perm-info">
                    <span className="ras-modal-perm-name">Editor</span>
                    <span className="ras-modal-perm-desc">Pode editar flashcards e apostilas</span>
                  </div>
                  <button type="button" className={`ras-sw${managingUser.isEditor ? ' on' : ''}`}
                    onClick={() => handleToggleEditor(managingUser._id, !managingUser.isEditor)}>
                    <span className="ras-sw-knob" />
                  </button>
                </div>
              </div>

              {/* Section D: Institution tags — edição administrativa direta,
                  nunca via grants/produto (ver toggleInstitutionTag acima) */}
              {knownInstitutionTags.length > 0 && (
                <div className="ras-modal-section">
                  <div className="ras-modal-section-label">Tags de instituição</div>
                  {knownInstitutionTags.map(tag => {
                    const checked = (managingUser.institutionTags || []).includes(tag);
                    return (
                      <div key={tag} className="ras-modal-perm-row">
                        <div className="ras-modal-perm-info">
                          <span className="ras-modal-perm-name">{tag}</span>
                          <span className="ras-modal-perm-desc">
                            Mostra o grupo marcado com essa tag na home, sem conceder acesso a conteúdo
                          </span>
                        </div>
                        <button type="button" className={`ras-sw${checked ? ' on' : ''}`}
                          onClick={() => toggleInstitutionTag(managingUser._id, tag, managingUser.institutionTags || [])}>
                          <span className="ras-sw-knob" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="ras-modal-footer">
              {mgRevokeConfirm ? (
                <div className="ras-modal-revoke-row">
                  <span className="ras-modal-revoke-text">Remover todos os produtos?</span>
                  <button className="ras-btn-danger-sm" onClick={() => handleRevokeAll(managingUser._id)}>Confirmar</button>
                  <button className="ras-icon-btn" onClick={() => setMgRevokeConfirm(false)}><X size={14} /></button>
                </div>
              ) : (
                <button className="ras-btn-danger-ghost" onClick={() => setMgRevokeConfirm(true)}>
                  <Trash2 size={13} /> Revogar todos os produtos
                </button>
              )}
              <button className="ras-btn-modal-close" onClick={closeManage}>Fechar</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ─── Aba Visão Geral ──────────────────────────────────────────────────────────

export const OverviewTab: React.FC = () => {
  const [overview, setOverview]   = useState<SaleOverview | null>(null);
  const [chartData, setChartData] = useState<ChartDay[]>([]);
  const [yearData, setYearData]   = useState<YearMonth[]>([]);
  const [trending, setTrending]   = useState<Trending | null>(null);
  const [period, setPeriod]       = useState<'7d' | '30d' | '90d'>('30d');
  const [year, setYear]           = useState(new Date().getFullYear());
  const [loading, setLoading]     = useState(true);
  const initDone = useRef(false);

  // Manual sale
  const [showAddSale, setShowAddSale]   = useState(false);
  const [saleEmail, setSaleEmail]       = useState('');
  const [saleProdId, setSaleProdId]     = useState('');
  const [salePrice, setSalePrice]       = useState('');
  const [saleDate, setSaleDate]         = useState(new Date().toISOString().slice(0, 10));
  const [saleNotes, setSaleNotes]       = useState('');
  const [saleCountRev, setSaleCountRev] = useState(true);
  const [saleSaving, setSaleSaving]     = useState(false);
  const [saleMsg, setSaleMsg]           = useState<{ ok: boolean; text: string } | null>(null);
  const [saleProducts, setSaleProducts] = useState<Product[]>([]);

  // Initial load (all at once, single spinner)
  useEffect(() => {
    Promise.all([
      api('/admin/sales/overview').then(r => r.ok ? r.json() : null),
      api(`/admin/sales/chart?period=${period}`).then(r => r.ok ? r.json() : []),
      api(`/admin/sales/year-chart?year=${year}`).then(r => r.ok ? r.json() : []),
      api('/admin/sales/trending').then(r => r.ok ? r.json() : null),
      api('/admin/products').then(r => r.ok ? r.json() : []),
    ]).then(([ov, ch, yr, tr, pr]) => {
      setOverview(ov); setChartData(ch || []); setYearData(yr || []);
      setTrending(tr); setSaleProducts(Array.isArray(pr) ? pr : pr.products || []);
      setLoading(false);
      initDone.current = true;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Period change: no flash, just swap chart data
  useEffect(() => {
    if (!initDone.current) return;
    api(`/admin/sales/chart?period=${period}`).then(r => r.ok ? r.json() : []).then(d => setChartData(d || []));
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  // Year change: no flash
  useEffect(() => {
    if (!initDone.current) return;
    api(`/admin/sales/year-chart?year=${year}`).then(r => r.ok ? r.json() : []).then(d => setYearData(d || []));
  }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddSale = async () => {
    if (!salePrice) return;
    setSaleSaving(true); setSaleMsg(null);
    try {
      const r = await api('/admin/sales/manual', {
        method: 'POST',
        body: JSON.stringify({
          email: saleEmail, productId: saleProdId || undefined,
          priceCents: Math.round(parseFloat(salePrice) * 100),
          notes: saleNotes, countAsRevenue: saleCountRev,
          grantedAt: saleDate,
        }),
      });
      if (r.ok) {
        setSaleMsg({ ok: true, text: 'Venda registrada com sucesso!' });
        setSaleEmail(''); setSaleProdId(''); setSalePrice(''); setSaleNotes('');
        setSaleDate(new Date().toISOString().slice(0, 10));
        const ov = await api('/admin/sales/overview').then(r2 => r2.ok ? r2.json() : null);
        if (ov) setOverview(ov);
      } else {
        const d = await r.json();
        setSaleMsg({ ok: false, text: d.message || 'Erro ao registrar venda.' });
      }
    } catch {
      setSaleMsg({ ok: false, text: 'Erro de conexão.' });
    } finally {
      setSaleSaving(false);
    }
  };

  if (loading) return <div className="ras-pane"><div className="ras-spinner-wrap"><div className="ras-spinner" /></div></div>;

  const maxBar = Math.max(...chartData.map(d => d.revenueCents), 1);

  return (
    <div className="ras-pane">
      {/* KPIs */}
      <div className="rasx-kpi-grid">
        <div className="ras-kpi" style={{ '--kpi-color': '#16a34a', '--kpi-bg': 'rgba(22,163,74,0.07)', '--kpi-border': 'rgba(22,163,74,0.28)' } as React.CSSProperties}>
          <div className="ras-kpi-top">
            <DollarSign size={15} className="ras-kpi-icon" />
            <div className="rasx-kpi-label">Receita total</div>
          </div>
          <div className="ras-kpi-value">{cents(overview?.totalRevenueCents || 0)}</div>
        </div>
        <div className="ras-kpi" style={{ '--kpi-color': '#2563eb', '--kpi-bg': 'rgba(37,99,235,0.07)', '--kpi-border': 'rgba(37,99,235,0.28)' } as React.CSSProperties}>
          <div className="ras-kpi-top">
            <ShoppingCart size={15} className="ras-kpi-icon" />
            <div className="rasx-kpi-label">Total de vendas</div>
          </div>
          <div className="ras-kpi-value">{overview?.totalSales || 0}</div>
        </div>
        <div className="ras-kpi" style={{ '--kpi-color': '#d97706', '--kpi-bg': 'rgba(217,119,6,0.07)', '--kpi-border': 'rgba(217,119,6,0.28)' } as React.CSSProperties}>
          <div className="ras-kpi-top">
            <TrendingUp size={15} className="ras-kpi-icon" />
            <div className="rasx-kpi-label">Receita hoje</div>
          </div>
          <div className="ras-kpi-value">{cents(overview?.revenueTodayCents || 0)}</div>
        </div>
        <div className="ras-kpi" style={{ '--kpi-color': '#7c3aed', '--kpi-bg': 'rgba(124,58,237,0.07)', '--kpi-border': 'rgba(124,58,237,0.28)' } as React.CSSProperties}>
          <div className="ras-kpi-top">
            <Users size={15} className="ras-kpi-icon" />
            <div className="rasx-kpi-label">Ticket médio</div>
          </div>
          <div className="ras-kpi-value">{cents(overview?.averageTicketCents || 0)}</div>
        </div>
      </div>

      {/* Bar chart */}
      <div className="ras-section">
        <div className="ras-section-header">
          <div className="ras-section-title">Vendas por período</div>
          <div className="ras-section-actions">
            <div className="ras-period-btns">
              {(['7d', '30d', '90d'] as const).map(p => (
                <button key={p} className={`ras-period-btn${period === p ? ' ras-period-btn--active' : ''}`} onClick={() => setPeriod(p)}>
                  {p === '7d' ? '7 dias' : p === '30d' ? '30 dias' : '90 dias'}
                </button>
              ))}
            </div>
            <button className="ras-add-btn" onClick={() => { setShowAddSale(true); setSaleMsg(null); }}>
              <PlusCircle size={14} /> Registrar venda
            </button>
          </div>
        </div>
        <div className="ras-bar-chart">
          {chartData.map((d, i) => (
            <div key={d.date} className="ras-bar-col">
              <div className="ras-bar-tooltip">{cents(d.revenueCents)}<br />{d.salesCount} venda{d.salesCount !== 1 ? 's' : ''}</div>
              <div className="ras-bar" style={{
                height: `${Math.max((d.revenueCents / maxBar) * 100, d.revenueCents > 0 ? 4 : 0)}%`,
                animationDelay: `${i * 30}ms`,
              }} />
              <div className="ras-bar-label">{d.date.slice(5)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Annual line chart */}
      <div className="ras-section">
        <div className="ras-section-header">
          <div className="ras-section-title">Evolução anual</div>
          <div className="ras-year-btns">
            <button className="ras-period-btn" onClick={() => setYear(y => y - 1)}><ChevronLeft size={14} /></button>
            <span className="ras-year-label">{year}</span>
            <button className="ras-period-btn" onClick={() => setYear(y => y + 1)}><ChevronRight size={14} /></button>
          </div>
        </div>
        <RyanMonthlyLineChart
          data={yearData.map(d => ({ month: d.month, salesCount: d.salesCount, valueCents: d.revenueCents }))}
          valueNoun="venda"
        />
      </div>

      {/* Trending */}
      {trending && (trending.rising.length > 0 || trending.falling.length > 0) && (
        <div className="ras-section">
          <div className="ras-section-title">Tendências (mês atual vs anterior)</div>
          <div className="ras-trending-grid">
            {trending.rising.slice(0, 3).map(t => (
              <div key={t.productId} className="ras-trend-card ras-trend-card--up">
                <ArrowUpRight size={16} />
                <span className="ras-trend-name">{t.title}</span>
                <span className="ras-trend-pct">+{t.growthPercent}%</span>
                <span className="ras-trend-sub">{t.previousSales} → {t.currentSales} vendas</span>
              </div>
            ))}
            {trending.falling.slice(0, 3).map(t => (
              <div key={t.productId} className="ras-trend-card ras-trend-card--down">
                <ArrowDownRight size={16} />
                <span className="ras-trend-name">{t.title}</span>
                <span className="ras-trend-pct">{t.growthPercent}%</span>
                <span className="ras-trend-sub">{t.previousSales} → {t.currentSales} vendas</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Histórico completo — antes era a aba própria "Histórico" da barra
          antiga; agora é o último bloco de "Vendas", depois de tudo que já
          existe aqui (KPIs, gráfico por período, evolução anual, tendências).
          HistoryTab continua sendo exatamente o mesmo componente, com toda a
          própria lógica (busca, filtro por produto, paginação) intacta — só
          passou a ser renderizado aqui em vez de atrás de uma aba separada. */}
      <HistoryTab />

      {/* Manual sale modal */}
      {showAddSale && ReactDOM.createPortal(
        <div className="ras-modal-overlay" onClick={() => setShowAddSale(false)}>
          <div className="ras-modal" onClick={e => e.stopPropagation()}>
            <div className="ras-modal-header">
              <div>
                <div className="ras-modal-title">Registrar venda</div>
                <div className="ras-modal-subtitle">Adicionar venda manualmente ao histórico</div>
              </div>
              <button className="ras-modal-close" onClick={() => setShowAddSale(false)}><X size={18} /></button>
            </div>
            <div className="ras-modal-body">
              <div className="ras-modal-section">
                <div className="ras-modal-section-label">Comprador <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '11px' }}>(opcional)</span></div>
                <input className="ras-input" placeholder="Nome ou email do comprador" value={saleEmail}
                  onChange={e => setSaleEmail(e.target.value)} />
              </div>
              <div className="ras-modal-section">
                <div className="ras-modal-section-label">Produto e valor</div>
                <select className="ras-input" value={saleProdId} onChange={e => setSaleProdId(e.target.value)}>
                  <option value="">Produto (opcional)</option>
                  {saleProducts.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
                </select>
                <input className="ras-input" type="number" step="0.01" placeholder="Valor em reais (ex: 190.00)"
                  value={salePrice} onChange={e => setSalePrice(e.target.value)} />
              </div>
              <div className="ras-modal-section">
                <div className="ras-modal-section-label">Detalhes</div>
                <input className="ras-input" type="date" value={saleDate} onChange={e => setSaleDate(e.target.value)} />
                <input className="ras-input" placeholder="Observações (opcional)" value={saleNotes}
                  onChange={e => setSaleNotes(e.target.value)} />
                <label className="ras-toggle-label">
                  <input type="checkbox" checked={saleCountRev} onChange={e => setSaleCountRev(e.target.checked)} />
                  Contar como receita
                </label>
              </div>
              {saleMsg && (
                <div className={`ras-msg${saleMsg.ok ? ' ras-msg--ok' : ' ras-msg--err'}`}>{saleMsg.text}</div>
              )}
            </div>
            <div className="ras-modal-footer">
              <button className="ras-btn-modal-close" onClick={() => setShowAddSale(false)}>Cancelar</button>
              <button className="ras-grant-btn" onClick={handleAddSale}
                disabled={saleSaving || !salePrice}>
                {saleSaving ? 'Salvando…' : <><PlusCircle size={14} /> Registrar</>}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ─── Aba Rentabilidade ────────────────────────────────────────────────────────

const EXPENSE_TYPES = [
  { value: 'infra', label: 'Infraestrutura' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'tools', label: 'Ferramentas' },
  { value: 'other', label: 'Outro' },
];
const RECURRENCES = [
  { value: 'once', label: 'Única' },
  { value: 'monthly', label: 'Mensal' },
  { value: 'yearly', label: 'Anual' },
];

export const ProfitabilityTab: React.FC = () => {
  const [profit, setProfit]     = useState<Profitability | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [chart, setChart]       = useState<ProfitChart[]>([]);
  const [chartPeriod, setChartPeriod] = useState<'week' | 'month' | 'year'>('month');
  const [typeFilter, setTypeFilter] = useState('all');
  const [loading, setLoading]   = useState(true);
  const profitInitDone = useRef(false);
  const typeFilterRef = useRef(typeFilter);
  typeFilterRef.current = typeFilter;

  // Expense form
  const [showForm, setShowForm]   = useState(false);
  const [editExp, setEditExp]     = useState<Expense | null>(null);
  const [fType, setFType]         = useState('infra');
  const [fDesc, setFDesc]         = useState('');
  const [fAmt, setFAmt]           = useState('');
  const [fRec, setFRec]           = useState('once');
  const [fDate, setFDate]         = useState('');
  const [fNotes, setFNotes]       = useState('');
  const [saving, setSaving]       = useState(false);

  // Initial load (single spinner, all at once)
  useEffect(() => {
    (async () => {
      setLoading(true);
      const [p, e, c] = await Promise.all([
        api('/admin/sales/profitability').then(r => r.ok ? r.json() : null),
        api('/admin/sales/expenses').then(r => r.ok ? r.json() : { expenses: [] }),
        api('/admin/sales/profitability-chart?period=month').then(r => r.ok ? r.json() : []),
      ]);
      setProfit(p); setExpenses(e.expenses || []); setChart(c || []);
      setLoading(false);
      profitInitDone.current = true;
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Chart period change: no flash
  useEffect(() => {
    if (!profitInitDone.current) return;
    api(`/admin/sales/profitability-chart?period=${chartPeriod}`).then(r => r.ok ? r.json() : []).then(d => setChart(d || []));
  }, [chartPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  // Type filter change: only reload expenses, no flash
  useEffect(() => {
    if (!profitInitDone.current) return;
    api(`/admin/sales/expenses${typeFilter !== 'all' ? `?type=${typeFilter}` : ''}`).then(r => r.ok ? r.json() : { expenses: [] }).then(d => setExpenses(d.expenses || []));
  }, [typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Silent full reload after save/delete
  const reload = useCallback(async () => {
    const tf = typeFilterRef.current;
    const [p, e] = await Promise.all([
      api('/admin/sales/profitability').then(r => r.ok ? r.json() : null),
      api(`/admin/sales/expenses${tf !== 'all' ? `?type=${tf}` : ''}`).then(r => r.ok ? r.json() : { expenses: [] }),
    ]);
    setProfit(p); setExpenses(e.expenses || []);
  }, []);

  const openNew = () => {
    setEditExp(null); setFType('infra'); setFDesc(''); setFAmt(''); setFRec('once');
    setFDate(new Date().toISOString().slice(0, 10)); setFNotes(''); setShowForm(true);
  };
  const openEdit = (e: Expense) => {
    setEditExp(e); setFType(e.type); setFDesc(e.description);
    setFAmt(String(e.amountCents / 100)); setFRec(e.recurrence);
    setFDate(e.date.slice(0, 10)); setFNotes(e.notes || ''); setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditExp(null); };

  const saveExpense = async () => {
    if (!fDesc || !fAmt || !fDate) return;
    setSaving(true);
    const body = { type: fType, description: fDesc, amountCents: Math.round(parseFloat(fAmt) * 100), recurrence: fRec, date: fDate, notes: fNotes };
    if (editExp) {
      await api(`/admin/sales/expenses/${editExp._id}`, { method: 'PUT', body: JSON.stringify(body) });
    } else {
      await api('/admin/sales/expenses', { method: 'POST', body: JSON.stringify(body) });
    }
    setSaving(false); closeForm(); reload();
  };

  const deleteExpense = async (id: string) => {
    if (!window.confirm('Excluir despesa?')) return;
    await api(`/admin/sales/expenses/${id}`, { method: 'DELETE' });
    reload();
  };

  const maxChart = Math.max(...chart.map(c => Math.max(c.revenue, c.expenses)), 1);

  if (loading) return <div className="ras-pane"><div className="ras-spinner-wrap"><div className="ras-spinner" /></div></div>;

  return (
    <div className="ras-pane">
      {/* KPIs */}
      <div className="rasx-kpi-grid">
        <div className="ras-kpi" style={{ '--kpi-color': '#16a34a', '--kpi-bg': 'rgba(22,163,74,0.07)', '--kpi-border': 'rgba(22,163,74,0.28)' } as React.CSSProperties}>
          <div className="ras-kpi-top"><TrendingUp size={15} className="ras-kpi-icon" /><div className="rasx-kpi-label">Receita total</div></div>
          <div className="ras-kpi-value">{cents(profit?.totalRevenueCents || 0)}</div>
        </div>
        <div className="ras-kpi" style={{ '--kpi-color': '#dc2626', '--kpi-bg': 'rgba(220,38,38,0.06)', '--kpi-border': 'rgba(220,38,38,0.25)' } as React.CSSProperties}>
          <div className="ras-kpi-top"><TrendingDown size={15} className="ras-kpi-icon" /><div className="rasx-kpi-label">Total de despesas</div></div>
          <div className="ras-kpi-value">{cents(profit?.totalExpensesCents || 0)}</div>
        </div>
        <div className="ras-kpi" style={{
          '--kpi-color': (profit?.netProfitCents || 0) >= 0 ? '#16a34a' : '#dc2626',
          '--kpi-bg': (profit?.netProfitCents || 0) >= 0 ? 'rgba(22,163,74,0.07)' : 'rgba(220,38,38,0.06)',
          '--kpi-border': (profit?.netProfitCents || 0) >= 0 ? 'rgba(22,163,74,0.28)' : 'rgba(220,38,38,0.25)',
        } as React.CSSProperties}>
          <div className="ras-kpi-top"><DollarSign size={15} className="ras-kpi-icon" /><div className="rasx-kpi-label">Lucro líquido</div></div>
          <div className="ras-kpi-value">{cents(profit?.netProfitCents || 0)}</div>
        </div>
        <div className="ras-kpi" style={{ '--kpi-color': '#7c3aed', '--kpi-bg': 'rgba(124,58,237,0.07)', '--kpi-border': 'rgba(124,58,237,0.25)' } as React.CSSProperties}>
          <div className="ras-kpi-top"><ArrowUpRight size={15} className="ras-kpi-icon" /><div className="rasx-kpi-label">Margem</div></div>
          <div className="ras-kpi-value">{profit?.margin || 0}%</div>
        </div>
      </div>

      {/* Unit economics */}
      <div className="ras-section">
        <div className="ras-section-title">Economia unitária ({profit?.totalSales || 0} vendas)</div>
        <div className="ras-unit-grid">
          <div className="ras-unit-card">
            <div className="ras-unit-label">Receita / venda</div>
            <div className="ras-unit-value ras-unit-value--green">{cents(profit?.avgRevenuePerSale || 0)}</div>
          </div>
          <div className="ras-unit-card">
            <div className="ras-unit-label">Custo / venda</div>
            <div className="ras-unit-value ras-unit-value--red">{cents(profit?.avgExpensePerSale || 0)}</div>
          </div>
          <div className="ras-unit-card">
            <div className="ras-unit-label">Lucro / venda</div>
            <div className={`ras-unit-value${(profit?.avgProfitPerSale || 0) >= 0 ? ' ras-unit-value--green' : ' ras-unit-value--red'}`}>
              {cents(profit?.avgProfitPerSale || 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Profitability chart */}
      <div className="ras-section">
        <div className="ras-section-header">
          <div className="ras-section-title">Receita vs Despesas</div>
          <div className="ras-period-btns">
            {(['week', 'month', 'year'] as const).map(p => (
              <button key={p} className={`ras-period-btn${chartPeriod === p ? ' ras-period-btn--active' : ''}`} onClick={() => setChartPeriod(p)}>
                {{ week: 'Semana', month: 'Mês', year: 'Ano' }[p]}
              </button>
            ))}
          </div>
        </div>
        <div className="ras-grouped-wrap">
          <div className="ras-grouped-chart">
            {chart.map((c, i) => (
              <div key={c.period} className="ras-grouped-col">
                <div className="ras-grouped-bars">
                  <div className="ras-grouped-bar ras-grouped-bar--rev"
                    style={{ height: `${(c.revenue / maxChart) * 100}%`, animationDelay: `${i * 40}ms` }}
                    title={`Receita: ${cents(c.revenue)}`} />
                  <div className="ras-grouped-bar ras-grouped-bar--exp"
                    style={{ height: `${(c.expenses / maxChart) * 100}%`, animationDelay: `${i * 40 + 15}ms` }}
                    title={`Despesas: ${cents(c.expenses)}`} />
                  <div className={`ras-grouped-bar ras-grouped-bar--prof${c.profit < 0 ? ' ras-grouped-bar--neg' : ''}`}
                    style={{ height: `${Math.abs(c.profit) / maxChart * 100}%`, animationDelay: `${i * 40 + 30}ms` }}
                    title={`Lucro: ${cents(c.profit)}`} />
                </div>
                <div className="ras-grouped-label">{c.period.slice(-5)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="ras-chart-legend">
          <span className="ras-legend-item ras-legend--rev">Receita</span>
          <span className="ras-legend-item ras-legend--exp">Despesas</span>
          <span className="ras-legend-item ras-legend--prof">Lucro</span>
        </div>
      </div>

      {/* Expenses table */}
      <div className="ras-section">
        <div className="ras-section-header">
          <div className="ras-section-title">Despesas</div>
          <div className="ras-section-actions">
            <select className="ras-input ras-input--sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="all">Todos os tipos</option>
              {EXPENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <button className="ras-add-btn" onClick={openNew}><Plus size={14} /> Nova despesa</button>
          </div>
        </div>

        {showForm && (
          <div className="ras-expense-form">
            <div className="ras-form-row">
              <select className="ras-input" value={fType} onChange={e => setFType(e.target.value)}>
                {EXPENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <input className="ras-input" placeholder="Descrição" value={fDesc} onChange={e => setFDesc(e.target.value)} />
              <input className="ras-input" type="number" placeholder="Valor (R$)" value={fAmt} onChange={e => setFAmt(e.target.value)} />
            </div>
            <div className="ras-form-row">
              <select className="ras-input" value={fRec} onChange={e => setFRec(e.target.value)}>
                {RECURRENCES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <input className="ras-input" type="date" value={fDate} onChange={e => setFDate(e.target.value)} />
              <input className="ras-input" placeholder="Observações (opcional)" value={fNotes} onChange={e => setFNotes(e.target.value)} />
              <button className="ras-grant-btn" onClick={saveExpense} disabled={saving}>{saving ? 'Salvando…' : editExp ? 'Salvar' : 'Adicionar'}</button>
              <button className="ras-icon-btn" onClick={closeForm}><X size={16} /></button>
            </div>
          </div>
        )}

        {expenses.length === 0
          ? <div className="ras-empty">Nenhuma despesa registrada.</div>
          : (
            <div className="ras-table-wrap">
              <table className="ras-table">
                <thead>
                  <tr>
                    <th>Tipo</th><th>Descrição</th><th>Valor</th><th>Recorrência</th><th>Data</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map(e => (
                    <tr key={e._id}>
                      <td><span className={`ras-type-badge ras-type-${e.type}`}>{EXPENSE_TYPES.find(t => t.value === e.type)?.label || e.type}</span></td>
                      <td>{e.description}{e.notes && <div className="ras-exp-note">{e.notes}</div>}</td>
                      <td className="ras-td-mono ras-td-red">{cents(e.amountCents)}</td>
                      <td>{RECURRENCES.find(r => r.value === e.recurrence)?.label || e.recurrence}</td>
                      <td className="ras-td-mono">{fmtDate(e.date)}</td>
                      <td>
                        <div className="ras-row-actions">
                          <button className="ras-icon-btn" onClick={() => openEdit(e)} title="Editar"><Edit2 size={13} /></button>
                          <button className="ras-icon-btn ras-icon-btn--danger" onClick={() => deleteExpense(e._id)} title="Excluir"><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
};

// ─── Aba Histórico ────────────────────────────────────────────────────────────

const HistoryTab: React.FC = () => {
  const [sales, setSales]       = useState<SaleEntry[]>([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [prodFilter, setProdFilter] = useState('');
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api('/admin/products').then(r => r.ok ? r.json() : { products: [] }).then(d => setProducts(d.products || d || []));
  }, []);

  const load = useCallback(async (p = page, q = search, pid = prodFilter) => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(p), limit: '20', search: q });
    if (pid) params.set('productId', pid);
    const r = await api(`/admin/sales/history?${params}`);
    if (r.ok) { const d = await r.json(); setSales(d.items || []); setTotal(d.total || 0); }
    setLoading(false);
  }, [page, search, prodFilter]);

  useEffect(() => { load(page, search, prodFilter); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); load(1, search, prodFilter); }, [search, prodFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="ras-section">
      <div className="ras-section-title">Histórico completo</div>
      <div className="ras-section-header">
        <div className="ras-filters-row">
          <input className="ras-input ras-input--search" placeholder="Buscar por email ou nome…" value={search} onChange={e => setSearch(e.target.value)} />
          <select className="ras-input ras-input--sm" value={prodFilter} onChange={e => setProdFilter(e.target.value)}>
            <option value="">Todos os produtos</option>
            {products.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
          </select>
        </div>
        <div className="ras-filters-right">
          <span className="ras-total-label">{total} venda{total !== 1 ? 's' : ''}</span>
          <button className="ras-icon-btn" onClick={() => load(page, search, prodFilter)}><RefreshCw size={15} /></button>
        </div>
      </div>

        {loading
          ? <div className="ras-spinner-wrap"><div className="ras-spinner" /></div>
          : sales.length === 0
          ? <div className="ras-empty">Nenhuma venda encontrada.</div>
          : (
            <div className="ras-table-wrap">
              <table className="ras-table">
                <thead>
                  <tr>
                    <th>Usuário</th><th>Produto</th><th>Valor</th><th>Duração</th><th>Status</th><th>Concedido por</th><th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map(s => (
                    <tr key={s._id}>
                      <td>
                        <div className="ras-user-name">{s.userId?.username || s.email}</div>
                        {s.userId?.username && <div className="ras-user-email">{s.email}</div>}
                      </td>
                      <td>{s.productId?.title || '—'}</td>
                      <td className="ras-td-mono">{cents(s.priceCents)}{!s.countAsRevenue && <span className="ras-no-rev"> (não conta)</span>}</td>
                      <td className="ras-history-duration">
                        {s.durationDays ? (
                          <>
                            <span className="ras-history-duration-days">{s.durationDays} dias</span>
                            {s.expiresAt && <span className="ras-history-duration-date">até {fmtDate(s.expiresAt)}</span>}
                          </>
                        ) : '—'}
                      </td>
                      <td>
                        <span className={`ras-status ras-status--${s.status}`}>
                          {{ completed: 'Concluído', pending: 'Pendente', revoked: 'Revogado' }[s.status] || s.status}
                        </span>
                      </td>
                      <td>{s.grantedBy?.username || 'Sistema'}</td>
                      <td className="ras-td-mono">{fmtDate(s.grantedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {totalPages > 1 && (
          <div className="ras-pagination">
            <button className="ras-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={16} /></button>
            <span className="ras-page-info">{page} / {totalPages}</span>
            <button className="ras-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={16} /></button>
          </div>
        )}
    </div>
  );
};
